// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { createReference, formatHumanName } from '@medplum/core';
import type { Communication, Patient, Practitioner, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconCircleCheck } from '@tabler/icons-react';
import { type JSX, useState } from 'react';
import {
  DME_COMMUNICATION_CATEGORY_SYSTEM,
  DME_ORDER_REFERENCE_OUTPUT_LABEL,
  DME_ORDER_SUBMITTED_CATEGORY_CODE,
  DME_ORDER_SUBMITTED_CATEGORY_DISPLAY,
  DME_TASK_BUSINESS_STATUS_URL,
  DME_TASK_BUSINESS_STATUSES,
} from '../../constants/dme';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';

interface FormValues {
  referenceNumber: string;
}

export interface MarkDmeSubmittedModalProps {
  task: Task;
  patient: Patient | undefined;
  opened: boolean;
  onClose: () => void;
  onSubmitted: (updatedTask: Task) => void;
  vendor?: string;
}

/**
 * Confirmation modal for the "Mark as submitted to Parachute" action. On
 * confirm it advances the Task to submitted/in-progress, stores the optional
 * vendor reference as Task.output, and posts an audit Communication.
 */
export function MarkDmeSubmittedModal({
  task,
  patient,
  opened,
  onClose,
  onSubmitted,
  vendor = 'Parachute',
}: MarkDmeSubmittedModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [saving, setSaving] = useState(false);

  const form = useForm<FormValues>({
    initialValues: { referenceNumber: '' },
  });

  const handleClose = (): void => {
    if (saving) {
      return;
    }
    form.reset();
    onClose();
  };

  const handleConfirm = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const reference = values.referenceNumber.trim();

      const submittedTask: Task = {
        ...task,
        status: 'in-progress',
        extension: [
          ...(task.extension ?? []).filter((extension) => extension.url !== DME_TASK_BUSINESS_STATUS_URL),
          { url: DME_TASK_BUSINESS_STATUS_URL, valueCode: DME_TASK_BUSINESS_STATUSES.submitted },
        ],
        ...(reference
          ? {
              output: [
                ...(task.output ?? []).filter((o) => o.type?.text !== DME_ORDER_REFERENCE_OUTPUT_LABEL),
                { type: { text: DME_ORDER_REFERENCE_OUTPUT_LABEL }, valueString: reference },
              ],
            }
          : {}),
      };
      const updatedTask = await medplum.updateResource<Task>(submittedTask);

      if (patient) {
        const providerName = profile?.name?.[0] ? formatHumanName(profile.name[0]) : 'a provider';
        const summary =
          `DME order submitted to ${vendor} by ${providerName} on ${new Date(now).toLocaleDateString()}.` +
          (reference ? ` ${vendor} reference: ${reference}.` : '');

        const communication: Communication = {
          resourceType: 'Communication',
          status: 'completed',
          sent: now,
          subject: createReference(patient),
          recipient: [createReference(patient)],
          sender: profile ? createReference(profile) : undefined,
          ...(task.encounter ? { encounter: task.encounter } : {}),
          category: [
            {
              coding: [
                {
                  system: DME_COMMUNICATION_CATEGORY_SYSTEM,
                  code: DME_ORDER_SUBMITTED_CATEGORY_CODE,
                  display: DME_ORDER_SUBMITTED_CATEGORY_DISPLAY,
                },
              ],
              text: DME_ORDER_SUBMITTED_CATEGORY_DISPLAY,
            },
          ],
          payload: [{ contentString: summary }],
        };
        await medplum.createResource<Communication>(withTenantScope(communication));
      }

      notifications.show({
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        title: 'Order submitted',
        message: 'The DME order task has been marked as submitted.',
      });
      form.reset();
      onSubmitted(updatedTask);
      onClose();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setSaving(false);
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title="Mark DME order as submitted" size="lg" centered>
      <Stack gap="md">
        <Text size="sm">
          Confirm you have submitted this DME order through {vendor}. This completes the task and posts an audit note to
          the patient chart.
        </Text>
        <TextInput
          label={`${vendor} order reference number`}
          placeholder="Optional — recorded for future reference"
          {...form.getInputProps('referenceNumber')}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => handleConfirm()} loading={saving}>
            Confirm submission
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
